/*
 * File: EventTree.ts
 * Author: Austin Robinson
 * Date Created: 9/27/2022
 * Copyright (c) DrivingDoc 2022
 */
import IntervalTree, { Interval } from '@flatten-js/interval-tree';

// import * as assert from 'assert';

// interface Resource {
//   id: string;
// }

interface Event {
  id: string;
  course_period_instructors?: {
    instructor_user_id: string;
  }[];
  start: string;
  end: string;
}

export default class EventTree<EventType extends Event> {
  public events: IntervalTree<string>; // tree to store all drives

  public resourceEvents: Map<string, IntervalTree<string>>; // trees to store resource's drives

  public resources: IntervalTree<string>; // tree to store all resources with drives

  private eventDictionary: Map<
    string,
    {
      // dictionary to store tree nodes for O(1) lookup time
      interval: Interval;
      event: EventType;
    }
  >;

  private calendarElementDictionary: Map<string, HTMLElement>; // dictionary to store the HTMLElement on the calendar

  public constructor() {
    this.events = new IntervalTree();
    this.resourceEvents = new Map();
    this.resources = new IntervalTree();
    this.eventDictionary = new Map();
    this.calendarElementDictionary = new Map();
  }

  public getAllEventIds(): string[] {
    return Array.from(this.eventDictionary.keys());
  }

  public removeEvent(id: string): boolean {
    const oldEventInfo = this.eventDictionary.get(id); // get the old event info

    // if the interval for the drive already exists
    if (oldEventInfo) {
      const instructorUserId = oldEventInfo.event.course_period_instructors?.[0]?.instructor_user_id;

      if (!instructorUserId) {
        throw new Error('Expected Instructor User ID But Got Null Or Undefined');
      }

      const resourceEventTree = this.resourceEvents.get(instructorUserId);

      if (!resourceEventTree) {
        throw new Error('Expected Resource Event Tree But Got Null Or Undefined');
      }

      const searchResults = [];
      searchResults[0] = this.events.search(oldEventInfo.interval).find((evnt) => evnt.id === oldEventInfo.event.id);
      searchResults[1] = resourceEventTree
        .search(oldEventInfo.interval)
        .find((evnt) => evnt.id === oldEventInfo.event.id);
      searchResults[2] = this.resources.search(oldEventInfo.interval).find((rsrc) => rsrc === instructorUserId);

      this.events.remove(oldEventInfo.interval, oldEventInfo.event.id);
      resourceEventTree.remove(oldEventInfo.interval, oldEventInfo.event.id);
      this.resources.remove(oldEventInfo.interval, instructorUserId);

      // if (!rmFromEventTree || !rmFromResourceEventTree || !rmFromResourceTree) {
      //   console.log('The Node Already Exists For Event ID: ', id);
      //   if (!rmFromEventTree) {
      //     console.log('Missing From Event Tree');
      //   }
      //   if (!rmFromResourceEventTree) {
      //     console.log('Missing From Resource Event Tree');
      //   }
      //   if (!rmFromResourceTree) {
      //     console.log('Missing From Resource Tree');
      //   }

      //   console.log('         Event Tree Remove: ', rmFromEventTree);
      //   console.log('Resource Event Tree Remove: ', rmFromResourceEventTree);
      //   console.log('      Resource Tree Remove: ', rmFromResourceTree);
      //   console.log('         Event Tree Search: ', searchResults[0]);
      //   console.log('Resource Event Tree Search: ', searchResults[1]);
      //   console.log('      Resource Tree Search: ', searchResults[2]);
      //   console.log(' ');
      // }
      this.eventDictionary.delete(id);
      return true;
    }
    return false;
  }

  public addNewEvent(event: EventType, isBTWPeriod: boolean, calendarElement?: HTMLElement) {
    event.course_period_instructors?.forEach((instructor) => {
      // check if the resource has an event tree
      if (!this.resourceEvents.has(instructor.instructor_user_id)) {
        // create a new event tree for resource
        this.resourceEvents.set(instructor.instructor_user_id, new IntervalTree());
        console.assert(this.resourceEvents.get(instructor.instructor_user_id), 'Resource Event Tree Is Empty');
      }
    });

    // create a new interval to insert into the drive tree
    const newInterval = new Interval(new Date(event.start).getTime() + 1, new Date(event.end).getTime() - 1);

    // remove the old event if it exists
    this.removeEvent(event.id);

    // insert the new event interval if it is a behind the wheel course
    // if (isBTWPeriod) {
    const newNode = this.events.insert(newInterval, event.id);
    console.assert(newNode.item.key, 'New Node Item Key Empty');
    console.assert(newNode.item.value, 'New Node Item Value Empty');

    if (!newNode) {
      throw new Error('Failed To Insert Into Event Tree');
    }
    // }

    const instructorUserId = event.course_period_instructors?.[0]?.instructor_user_id;
    if (!instructorUserId) {
      throw new Error('Expected Instructor User ID But Got Null Or Undefined');
    }

    // insert the event interval
    if (!this.resourceEvents.get(instructorUserId)?.insert(newInterval, event.id)) {
      throw new Error('Failed To Insert Into Resource Event Tree');
    }

    event.course_period_instructors?.forEach((instructor) => {
      // insert the resource interval
      if (isBTWPeriod && !this.resources.insert(newInterval, instructor?.instructor_user_id ?? null)) {
        throw new Error('Failed To Insert Into Resource Tree');
      }
    });

    // if calendarElement is not null or undefined
    if (calendarElement) {
      // update the element in the dictionary
      this.calendarElementDictionary.set(event.id, calendarElement);
    }

    // store reference to new node in a dictionary
    this.eventDictionary.set(event.id, {
      interval: newInterval,
      event,
    });
  }

  public getEventsByResource(resourceID: string, interval: Interval): EventType[] {
    if (!this.resourceEvents.has(resourceID)) {
      return [];
    }
    return this.resourceEvents.get(resourceID)?.search(interval) as EventType[];
  }
}
